import { useState } from "react";
import { CommunityInsightsData } from "../../hooks/useCommunityInsights";
import "./MostAffectedCommunities.css";

interface MostAffectedCommunitiesProps {
  communities: CommunityInsightsData["communities"];
  periodLabel: string;
}

const ARROWS = {
  up: "↑",
  down: "↓",
  flat: "→",
};

export function MostAffectedCommunities({
  communities,
  periodLabel,
}: MostAffectedCommunitiesProps) {
  const [sortBy, setSortBy] = useState<
    "outageFrequency" | "peakCustomersOut" | "totalOutageHours"
  >("outageFrequency");

  if (!communities || communities.length === 0) {
    return (
      <div className="MostAffectedCommunities">
        <div className="MostAffectedCommunities-empty">
          No community outage data for this period
        </div>
      </div>
    );
  }

  // Sort communities based on current sort column
  const sortedCommunities = [...communities].sort((a, b) => {
    return b.metrics[sortBy] - a.metrics[sortBy];
  });

  const handleSort = (column) => {
    setSortBy(column);
  };

  const getChangeClass = (direction, isGood) => {
    if (direction === "up")
      return isGood
        ? "MostAffectedCommunities-change--positive"
        : "MostAffectedCommunities-change--negative";
    if (direction === "down")
      return isGood
        ? "MostAffectedCommunities-change--negative"
        : "MostAffectedCommunities-change--positive";
    return "";
  };

  const renderChange = (comparison, isGood) => {
    if (!comparison) return null;

    return (
      <span className={getChangeClass(comparison.direction, isGood)}>
        {" "}
        {ARROWS[comparison.direction]}
        {comparison.change > 0 ? "+" : ""}
        {comparison.change}
      </span>
    );
  };

  const renderHoursChange = (comparison) => {
    if (!comparison) return null;

    const changeHours = Math.round(comparison.change);
    const changeDisplay = changeHours > 0 ? `+${changeHours}h` : `${changeHours}h`;

    return (
      <span className={getChangeClass(comparison.direction, false)}>
        {" "}
        {ARROWS[comparison.direction]}
        {changeDisplay}
      </span>
    );
  };

  return (
    <div className="MostAffectedCommunities">
      <h3 className="MostAffectedCommunities-title">Communities Affected</h3>
      <table className="MostAffectedCommunities-table">
        <thead>
          <tr>
            <th className="MostAffectedCommunities-th--rank">Rank</th>
            <th className="MostAffectedCommunities-th--community">Community</th>
            <th className="MostAffectedCommunities-th--county">County</th>
            <th
              className={`MostAffectedCommunities-th--sortable ${sortBy === "outageFrequency" ? "MostAffectedCommunities-th--active" : ""}`}
              onClick={() => handleSort("outageFrequency")}
            >
              Outages
              {sortBy === "outageFrequency" && (
                <span className="MostAffectedCommunities-sortIndicator">
                  {" "}
                  ↓
                </span>
              )}
            </th>
            <th
              className={`MostAffectedCommunities-th--sortable ${sortBy === "peakCustomersOut" ? "MostAffectedCommunities-th--active" : ""}`}
              onClick={() => handleSort("peakCustomersOut")}
            >
              Peak Customers
              {sortBy === "peakCustomersOut" && (
                <span className="MostAffectedCommunities-sortIndicator">
                  {" "}
                  ↓
                </span>
              )}
            </th>
            <th
              className={`MostAffectedCommunities-th--sortable ${sortBy === "totalOutageHours" ? "MostAffectedCommunities-th--active" : ""}`}
              onClick={() => handleSort("totalOutageHours")}
            >
              Outage Hours
              {sortBy === "totalOutageHours" && (
                <span className="MostAffectedCommunities-sortIndicator">
                  {" "}
                  ↓
                </span>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedCommunities.map((community, index) => (
            <tr key={community.name}>
              <td className="MostAffectedCommunities-td--rank">{index + 1}</td>
              <td className="MostAffectedCommunities-td--community">
                {community.name}
              </td>
              <td className="MostAffectedCommunities-td--county">
                {community.county}
              </td>
              <td className="MostAffectedCommunities-td--metric">
                {community.metrics.outageFrequency.toLocaleString()}
                {renderChange(community.comparisons.outageFrequency, false)}
              </td>
              <td className="MostAffectedCommunities-td--metric">
                {community.metrics.peakCustomersOut.toLocaleString()}
                {renderChange(community.comparisons.peakCustomersOut, false)}
              </td>
              <td className="MostAffectedCommunities-td--metric">
                {Math.round(community.metrics.totalOutageHours).toLocaleString()}h
                {renderHoursChange(community.comparisons.totalOutageHours)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
